import React, { useState, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';

interface WorldMapProps {
  data: Array<{
    country: string;
    flag: string;
    views: number;
  }>;
  activeTab?: string;
}

const WorldMap: React.FC<WorldMapProps> = ({ data, activeTab = 'hubs' }) => {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
    visible: boolean;
  }>({
    content: '',
    x: 0,
    y: 0,
    visible: false
  });
  
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });

  const handleReset = () => {
    setTransform({ x: 0, y: 0, scale: 1 });
  };

  // 调试信息
  console.log('WorldMap received data:', data);
  
  // 计算最大浏览量用于归一化
  const maxViews = Math.max(...data.map(item => item.views));
  const minViews = Math.min(...data.map(item => item.views));

  console.log('WorldMap maxViews:', maxViews, 'minViews:', minViews);

  // 根据activeTab获取对应的单位
  const getUnitForTab = (tab: string) => {
    switch (tab) {
      case 'hubs':
        return 'hubs';
      case 'projects':
        return 'projects';
      case 'accUsers':
        return 'ACC users';
      case 'appUsers':
        return 'app users';
      default:
        return 'hubs';
    }
  };

  // 根据浏览量获取颜色和工具提示内容
  const getCountryInfo = (countryName: string) => {
    // 国家名称映射，处理地图数据中的不同命名
    const countryNameMap: { [key: string]: string } = {
      'United States of America': 'United States',
      'USA': 'United States',
      'The Bahamas': 'Bahamas',
      'Côte d\'Ivoire': 'Ivory Coast',
      'Czech Republic': 'Czechia',
      'Democratic Republic of the Congo': 'Congo',
      'Republic of the Congo': 'Congo',
      'Dominican Republic': 'Dominican Republic',
      'Equatorial Guinea': 'Equatorial Guinea',
      'Federated States of Micronesia': 'Micronesia',
      'Marshall Islands': 'Marshall Islands',
      'Solomon Islands': 'Solomon Islands',
      'United Arab Emirates': 'UAE',
      'United Kingdom of Great Britain and Northern Ireland': 'United Kingdom',
      'UK': 'United Kingdom',
      'Great Britain': 'United Kingdom',
      'Vatican City State': 'Vatican',
      'Holy See': 'Vatican'
    };

    // 尝试直接匹配
    let countryData = data.find(item => 
      item.country.toLowerCase() === countryName.toLowerCase()
    );

    // 如果没有直接匹配，尝试映射
    if (!countryData) {
      const mappedName = countryNameMap[countryName];
      if (mappedName) {
        countryData = data.find(item => 
          item.country.toLowerCase() === mappedName.toLowerCase()
        );
      }
    }

    // 如果还是没有找到，尝试部分匹配
    if (!countryData) {
      countryData = data.find(item => 
        countryName.toLowerCase().includes(item.country.toLowerCase()) ||
        item.country.toLowerCase().includes(countryName.toLowerCase())
      );
    }
    
    if (!countryData) {
      const unit = getUnitForTab(activeTab);
      return {
        color: "#e5e7eb", // 浅灰色，没有数据
        tooltip: `${countryName}: 0 ${unit}`
      };
    }
    
    // 越多的数据越绿 - 使用红色到绿色渐变
    let intensity;
    let hue;
    
    // 处理当所有数据都相同时的情况（避免除以0导致的NaN）
    if (maxViews === minViews) {
      // 如果所有数据都相同，使用最亮的绿色（因为这是唯一有数据的国家）
      intensity = 1.0;
      hue = 120; // 最亮的绿色
    } else {
      intensity = (countryData.views - minViews) / (maxViews - minViews);
      // 从红色(0)到绿色(120)的渐变
      hue = intensity * 120; // 0(红色) -> 120(绿色)
    }
    
    const saturation = 70; // 70%
    const lightness = 50; // 固定亮度，让颜色更鲜艳
    
    const unit = getUnitForTab(activeTab);
    return {
      color: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      tooltip: `${countryName}: ${countryData.views} ${unit}`
    };
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{
          scale: 200,
          center: [0, 0] // 调整中心，确保地图完整显示
        }}
        style={{
          width: "100%",
          height: "100%"
        }}
      >
        <ZoomableGroup
          center={[transform.x, transform.y]}
          zoom={transform.scale}
          onMoveEnd={(position) => {
            setTransform({
              x: position.coordinates[0],
              y: position.coordinates[1],
              scale: position.zoom
            });
          }}
        >
          {/* 世界地图地理数据 */}
          <Geographies
            geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json"
            fill="#e2e8f0"
            stroke="#cbd5e1"
            strokeWidth={0.5}
          >
            {({ geographies }) =>
              geographies
                .filter((geo) => {
                  const countryName = geo.properties.name;
                  
                  // 调试：打印所有国家名称，找到夏威夷的确切名称
                  console.log('Country name:', countryName);
                  
                  // 排除夏威夷和其他偏远地区，但保留澳大利亚和新西兰
                  const excludedRegions = [
                    'Hawaii', 'Alaska', 'Guam', 'Puerto Rico', 'American Samoa',
                    'Northern Mariana Islands', 'U.S. Virgin Islands',
                    'French Polynesia', 'New Caledonia', 'Wallis and Futuna',
                    'Cook Islands', 'Niue', 'Tokelau', 'Pitcairn Islands',
                    'Hawaiian Islands', 'State of Hawaii', 'Hawai\'i'
                  ];
                  
                  // 确保澳大利亚和新西兰不被排除
                  if (countryName === 'Australia' || countryName === 'New Zealand') {
                    return true;
                  }
                  
                  // 更严格的夏威夷检查
                  if (countryName.toLowerCase().includes('hawaii') || 
                      countryName.toLowerCase().includes('hawai\'i') ||
                      countryName.toLowerCase().includes('hawaiian')) {
                    console.log('Excluding Hawaii:', countryName);
                    return false;
                  }
                  
                  // 排除南极
                  if (countryName.toLowerCase().includes('antarctica') || 
                      countryName.toLowerCase().includes('antarctic')) {
                    console.log('Excluding Antarctica:', countryName);
                    return false;
                  }
                  
                  // 通过地理坐标排除夏威夷和南极
                  try {
                    const coordinates = geo.geometry.coordinates;
                    if (coordinates && Array.isArray(coordinates)) {
                      // 检查是否在夏威夷附近的地理区域
                      const isInHawaiiRegion = coordinates.some(coord => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                          const [lng, lat] = coord;
                          // 夏威夷的大致地理范围
                          return lat >= 18 && lat <= 23 && lng >= -161 && lng <= -154;
                        }
                        return false;
                      });
                      
                      if (isInHawaiiRegion) {
                        console.log('Excluding Hawaii by coordinates:', countryName);
                        return false;
                      }
                      
                      // 检查是否在南极附近的地理区域
                      const isInAntarcticaRegion = coordinates.some(coord => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                          const [lng, lat] = coord;
                          // 南极的大致地理范围（南纬60度以南）
                          return lat <= -60;
                        }
                        return false;
                      });
                      
                      if (isInAntarcticaRegion) {
                        console.log('Excluding Antarctica by coordinates:', countryName);
                        return false;
                      }
                    }
                  } catch (error) {
                    // 忽略坐标解析错误
                  }
                  
                  return !excludedRegions.some(region => 
                    countryName.toLowerCase().includes(region.toLowerCase())
                  );
                })
                .map((geo) => {
                  const countryName = geo.properties.name;
                  const countryInfo = getCountryInfo(countryName);
                  
                  // 调试信息：打印前几个国家的名称
                  if (['United States', 'India', 'Germany', 'United Kingdom', 'France', 'Australia', 'New Zealand'].includes(countryName)) {
                    console.log(`Map country: "${countryName}" -> Color: ${countryInfo.color}`);
                  }
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: {
                          fill: countryInfo.color,
                          stroke: "#cbd5e1",
                          strokeWidth: 0.5,
                          outline: "none",
                        },
                        hover: {
                          fill: countryInfo.color,
                          stroke: "#94a3b8",
                          strokeWidth: 1,
                          outline: "none",
                        },
                        pressed: {
                          fill: countryInfo.color,
                          stroke: "#64748b",
                          strokeWidth: 1,
                          outline: "none",
                        },
                      }}
                      onMouseEnter={(evt) => {
                        const rect = evt.currentTarget.getBoundingClientRect();
                        const containerRect = evt.currentTarget.closest('.relative')?.getBoundingClientRect();
                        const x = evt.clientX - (containerRect?.left || rect.left);
                        const y = evt.clientY - (containerRect?.top || rect.top);
                        setTooltip({
                          content: countryInfo.tooltip,
                          x: x,
                          y: y,
                          visible: true
                        });
                      }}
                      onMouseMove={(evt) => {
                        const containerRect = evt.currentTarget.closest('.relative')?.getBoundingClientRect();
                        const x = evt.clientX - (containerRect?.left || 0);
                        const y = evt.clientY - (containerRect?.top || 0);
                        setTooltip(prev => ({
                          ...prev,
                          x: x,
                          y: y
                        }));
                      }}
                      onMouseLeave={() => {
                        setTooltip(prev => ({ ...prev, visible: false }));
                      }}
                    />
                  );
                })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Reset Button */}
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Reset
        </Button>
      </div>

      {/* 图例 */}
      <div className="absolute bottom-4 left-2 bg-white rounded p-2 shadow-lg border text-xs">
        <div className="text-xs font-semibold text-gray-700 mb-2">
          {activeTab === 'hubs' && 'Hub Distribution'}
          {activeTab === 'projects' && 'Project Distribution'}
          {activeTab === 'accUsers' && 'ACC User Distribution'}
          {activeTab === 'appUsers' && 'App User Distribution'}
        </div>
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-24 h-2 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded"></div>
          <div className="text-xs text-gray-500">
            {minViews}-{maxViews}
          </div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-red-500 rounded"></div>
            <span className="text-gray-600">Low</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded"></div>
            <span className="text-gray-600">High</span>
          </div>
        </div>
      </div>

      {/* 工具提示 */}
      {tooltip.visible && (
        <div 
          className="absolute text-white px-2 py-1 rounded text-xs shadow-lg z-50 pointer-events-none"
          style={{
            left: Math.min(tooltip.x + 10, 600), // 限制在容器内
            top: Math.max(tooltip.y - 35, 5),
            backgroundColor: 'rgba(75, 85, 99, 0.9)',
            backdropFilter: 'blur(4px)',
            maxWidth: '150px'
          }}
        >
          {tooltip.content}
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
            style={{ borderTopColor: 'rgba(75, 85, 99, 0.9)' }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default WorldMap;
